import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import {
  addIssue,
  applyRealtime,
  castVote,
  createRoom,
  deleteIssue,
  getRoomSnapshot,
  joinRoom,
  markLeaving,
  markPresent,
  revealCards,
  startIssue,
  startNewRound,
  type Issue,
  type Player,
  type Room,
  type RoomSettings,
  type RoomSnapshot,
  type Vote,
} from "@/lib/api/rooms";
import { ensureGuest } from "@/lib/guest";
import { pb } from "@/lib/pocketbase";

export const roomKeys = {
  guest: ["guest"] as const,
  detail: (roomId: string) => ["rooms", roomId] as const,
};

/** The anonymous guest's user id (created on the first visit, see lib/guest.ts). */
export function useGuest() {
  return useQuery({ queryKey: roomKeys.guest, queryFn: ensureGuest, staleTime: Infinity });
}

/** The room, kept live: realtime events are folded into the cached snapshot. */
export function useRoom(roomId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: roomKeys.detail(roomId),
    queryFn: () => getRoomSnapshot(roomId),
    enabled,
  });

  useEffect(() => {
    if (!enabled) return;
    const key = roomKeys.detail(roomId);
    const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
    const update = (change: (snapshot: RoomSnapshot) => RoomSnapshot) => {
      queryClient.setQueryData<RoomSnapshot | null>(key, (snapshot) =>
        snapshot ? change(snapshot) : snapshot,
      );
      // A fetch that started before this event would land with older data and overwrite it;
      // restart it so the newest state wins.
      if (queryClient.isFetching({ queryKey: key }) > 0) refetch();
    };
    const options = { filter: pb.filter("room = {:roomId}", { roomId }) };
    let connected = false;

    const subscriptions = [
      pb.collection("rooms").subscribe<Room>(roomId, ({ record }) => {
        const before = queryClient.getQueryData<RoomSnapshot | null>(key)?.room;
        update((snapshot) => ({ ...snapshot, room: record }));
        // Revealing makes everyone's votes readable and a new round deletes them; the API
        // rules decide what we may see, so ask again rather than guess.
        if (before && before.state !== record.state) refetch();
      }),
      pb
        .collection("players")
        .subscribe<Player>(
          "*",
          (event) => update((s) => ({ ...s, players: applyRealtime(s.players, event) })),
          options,
        ),
      pb
        .collection("issues")
        .subscribe<Issue>(
          "*",
          (event) => update((s) => ({ ...s, issues: applyRealtime(s.issues, event) })),
          options,
        ),
      pb
        .collection("votes")
        .subscribe<Vote>(
          "*",
          (event) => update((s) => ({ ...s, votes: applyRealtime(s.votes, event) })),
          options,
        ),
      // After a dropped connection, catch up on whatever happened meanwhile.
      pb.realtime.subscribe("PB_CONNECT", () => {
        if (connected) refetch();
        connected = true;
      }),
    ].map((subscription) => subscription.catch(() => undefined));

    return () => {
      for (const subscription of subscriptions)
        void subscription.then((unsubscribe) => unsubscribe?.());
    };
  }, [enabled, roomId, queryClient]);

  return query;
}

/** Keeps the player marked as present while the room is open. */
export function usePresence(playerId: string | undefined) {
  useEffect(() => {
    if (!playerId) return;
    const beat = () => void markPresent(playerId).catch(() => undefined);
    const leave = () => markLeaving(playerId);
    beat();
    const timer = window.setInterval(beat, 30_000);
    window.addEventListener("pagehide", leave);
    window.addEventListener("pageshow", beat);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", beat);
    };
  }, [playerId]);
}

const showError = (error: Error) => toast.error(error.message || "Something went wrong");

export function useCreateRoom() {
  return useMutation({
    mutationFn: async (settings: RoomSettings) => createRoom(settings, await ensureGuest()),
    onSuccess: (room) => track("room_created", { voting_system: room.voting_system }),
  });
}

export function useJoinRoom(roomId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => joinRoom(roomId, await ensureGuest(), name),
    onSuccess: () => {
      track("room_joined");
      // Players, issues and votes were hidden until now.
      return queryClient.invalidateQueries({ queryKey: roomKeys.detail(roomId) });
    },
  });
}

export function useCastVote(roomId: string) {
  return useMutation({
    mutationFn: (value: string) => castVote(roomId, value),
    onError: showError,
  });
}

export function useRevealCards(roomId: string) {
  return useMutation({
    mutationFn: () => revealCards(roomId),
    onSuccess: () => track("cards_revealed"),
    onError: showError,
  });
}

export function useStartNewRound(roomId: string) {
  return useMutation({ mutationFn: () => startNewRound(roomId), onError: showError });
}

export function useStartIssue(roomId: string) {
  return useMutation({
    mutationFn: (issueId: string) => startIssue(roomId, issueId),
    onError: showError,
  });
}

export function useAddIssue(roomId: string) {
  return useMutation({
    mutationFn: (title: string) => addIssue(roomId, title),
    onSuccess: () => track("issue_added"),
  });
}

export function useDeleteIssue() {
  return useMutation({ mutationFn: deleteIssue, onError: showError });
}
